// Restoring an expired session without sending the user to /signup.
//
//   refresh  - the stored session has a refresh token: renew it in place
//              (oidc-client-ts refresh-token grant; no redirect, no UI).
//   redirect - no refresh token: one full-page trip to Cognito with
//              prompt=none. With managed login, an active Cognito session
//              comes straight back signed in and anything else comes back
//              with login_required; the classic hosted UI ignores prompt and
//              shows its sign-in page instead.
//   none     - go to /signup.
//
// Only a RETURNING user (a stored session that expired) is tried; a
// first-time or signed-out visitor goes straight to /signup. A per-tab
// marker in sessionStorage allows one attempt per SSO_RETRY_AFTER_MS, so a
// failed attempt can never redirect in a loop; if the marker cannot be read
// or written, no attempt is made.

export const SSO_ATTEMPT_KEY = 'tickrtrends_sso_attempt';
export const SSO_RETRY_AFTER_MS = 10 * 60 * 1000;

/**
 * @param {{ user: { refresh_token?: string } | null | undefined,
 *           lastAttemptAt: number | null, now: number }} input
 * @returns {'refresh' | 'redirect' | 'none'}
 */
export const silentSignInPlan = ({ user, lastAttemptAt, now }) => {
  if (lastAttemptAt !== null && now - lastAttemptAt < SSO_RETRY_AFTER_MS) return 'none';
  if (!user) return 'none';
  return user.refresh_token ? 'refresh' : 'redirect';
};

/** @returns {number | null} null = never tried; +Infinity = unreadable (treat as just tried) */
export const lastSsoAttempt = () => {
  try {
    const raw = window.sessionStorage.getItem(SSO_ATTEMPT_KEY);
    if (raw === null) return null;
    const at = Number(raw);
    return Number.isFinite(at) ? at : Number.POSITIVE_INFINITY;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
};

/** @returns {boolean} false when the marker could not be written (then don't attempt) */
export const markSsoAttempt = (now = Date.now()) => {
  try {
    window.sessionStorage.setItem(SSO_ATTEMPT_KEY, String(now));
    return window.sessionStorage.getItem(SSO_ATTEMPT_KEY) === String(now);
  } catch {
    return false;
  }
};

export const clearSsoAttempt = () => {
  try {
    window.sessionStorage.removeItem(SSO_ATTEMPT_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
};
